import { useState } from "react";
import { X, Shield, Key, Lock } from "lucide-react";
import { OpenAPISpec } from "../types";

interface SecurityModalProps {
  spec: OpenAPISpec;
  credentials: Record<string, string>;
  onSave: (credentials: Record<string, string>) => void;
  onClose: () => void;
}

export function SecurityModal({
  spec,
  credentials,
  onSave,
  onClose,
}: SecurityModalProps) {
  const [localCredentials, setLocalCredentials] =
    useState<Record<string, string>>(credentials);

  const securitySchemes =
    spec.components?.securitySchemes || spec.securityDefinitions || {};
  const schemeEntries = Object.entries(securitySchemes);

  const handleChange = (name: string, value: string) => {
    setLocalCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSave(localCredentials);
    onClose();
  };

  if (schemeEntries.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200/60 bg-zinc-50/50">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-zinc-700" />
              <h2 className="text-lg font-semibold text-zinc-900 tracking-tight">
                Authentication
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-8 text-center text-zinc-500">
            No security schemes defined in this API specification.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-full flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200/60 bg-zinc-50/50">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-zinc-700" />
            <h2 className="text-lg font-semibold text-zinc-900 tracking-tight">
              Authentication
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {schemeEntries.map(([name, scheme]: [string, any]) => {
            const type = scheme.type;
            const description = scheme.description;

            let label = name;
            let placeholder = "Value";
            let icon = <Key className="w-4 h-4 text-zinc-400" />;

            if (type === "apiKey") {
              label = `API Key (${scheme.name} in ${scheme.in})`;
            } else if (type === "http") {
              label = `HTTP ${scheme.scheme}`;
              placeholder =
                scheme.scheme === "bearer"
                  ? "Bearer Token"
                  : "Username:Password (Base64)";
              icon = <Lock className="w-4 h-4 text-zinc-400" />;
            } else if (type === "oauth2") {
              label = "OAuth2 Token";
              placeholder = "Bearer Token";
            } else if (type === "openIdConnect") {
              label = "OpenID Connect Token";
              placeholder = "Bearer Token";
            }

            return (
              <div key={name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                    {icon}
                    {label}
                  </label>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded">
                    {type}
                  </span>
                </div>
                {description && (
                  <p className="text-xs text-zinc-500 mb-2">{description}</p>
                )}
                <div className="relative">
                  <input
                    type="text"
                    value={localCredentials[name] || ""}
                    onChange={(e) => handleChange(name, e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 bg-white border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-6 border-t border-zinc-200/60 bg-zinc-50/30 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-zinc-900 text-white text-sm font-medium rounded-xl hover:bg-zinc-800 transition-colors shadow-sm"
          >
            Save Credentials
          </button>
        </div>
      </div>
    </div>
  );
}
